import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import {
  fetchDealerListing,
  runDealerListingAction,
} from "../../services/automapDealerInventoryApi";
import { fetchListingFairPrice } from "../../services/automapFairPriceApi";
import { fetchListingVehicleRisk } from "../../services/automapVehicleRiskApi";
import { fetchDealerFeaturedPortal, requestFeaturedPlacement } from "../../services/automapFeaturedPlacementApi";
import { trackFeaturedPlacementEvent } from "../../constants/automapFeaturedPlacementConstants";
import FairPriceBadge from "../../components/automap/FairPriceBadge";
import { RiskLevelPill } from "../../components/automap/RiskBadge";
import { getDealerFixSuggestion } from "../../utils/vehicleRiskDisplay";
import "../../styles/riskBadge.css";
import { FAIR_PRICE_LABELS_ES } from "../../constants/automapFairPriceConstants";
import { trackFairPriceEvent } from "../../utils/fairPriceAnalytics";
import {
  qualityBadgeClass,
  trackInventoryEvent,
} from "../../constants/automapDealerInventoryConstants";
import { LISTING_PLACEHOLDER_IMAGE } from "../../constants/automapListingConstants";
import DealerListingBuyerInterestSection from "../../components/dealer/DealerListingBuyerInterestSection";
import DealerListingPhotoEditor from "../../components/dealer/DealerListingPhotoEditor";
import DealerListingPublicAction from "../../components/dealer/DealerListingPublicAction";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import { useCurrency, useLocale } from "../../intl";

function listingTitle(listing) {
  return [listing.year, listing.make, listing.model, listing.version].filter(Boolean).join(" ");
}

function statusTone(status) {
  if (status === "active") return "active";
  if (status === "sold" || status === "archived") return "muted";
  if (status === "paused") return "paused";
  return "neutral";
}

function formatDate(value, locale) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return value;
  }
}

function DetailSkeleton() {
  return (
    <div className="dealer-listing-detail dealer-listing-detail--loading" aria-busy="true">
      <div className="dealer-listing-detail__skeleton dealer-listing-detail__skeleton--hero" />
      <div className="dealer-listing-detail__skeleton-row">
        <div className="dealer-listing-detail__skeleton dealer-listing-detail__skeleton--kpi" />
        <div className="dealer-listing-detail__skeleton dealer-listing-detail__skeleton--kpi" />
        <div className="dealer-listing-detail__skeleton dealer-listing-detail__skeleton--kpi" />
        <div className="dealer-listing-detail__skeleton dealer-listing-detail__skeleton--kpi" />
      </div>
    </div>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <article className="dealer-listing-detail__stat">
      <span className="dealer-listing-detail__stat-label">{label}</span>
      <strong className="dealer-listing-detail__stat-value">{value}</strong>
      {hint ? <small className="dealer-listing-detail__stat-hint">{hint}</small> : null}
    </article>
  );
}

function SpecItem({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="dealer-listing-detail__spec">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export default function DealerInventoryDetailPage() {
  const { listingId } = useParams();
  const { dealerId } = useOutletContext();
  const { locale } = useLocale();
  const { formatMoney } = useCurrency();
  const { t, formatListingStatus, translateError } = useDealerPortalIntl();
  const [listing, setListing] = useState(null);
  const [fairPrice, setFairPrice] = useState(null);
  const [vehicleRisk, setVehicleRisk] = useState(null);
  const [featuredInfo, setFeaturedInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const [photoEditorError, setPhotoEditorError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchDealerListing(dealerId, listingId);
      setListing(data.listing);
      setSelectedPhoto(0);
      try {
        const snapshot = await fetchListingFairPrice(listingId);
        setFairPrice(snapshot);
        trackFairPriceEvent("dealer_fair_price_insight_viewed", { listingId, dealerId });
      } catch {
        setFairPrice(null);
      }
      try {
        const risk = await fetchListingVehicleRisk(listingId, "dealer");
        setVehicleRisk(risk);
      } catch {
        setVehicleRisk(null);
      }
      try {
        const featured = await fetchDealerFeaturedPortal(dealerId);
        const active = (featured.featured?.activePlacements || []).find((p) => p.listingId === listingId);
        setFeaturedInfo(active || null);
      } catch {
        setFeaturedInfo(null);
      }
    } catch (err) {
      setError(translateError(err.message, "generic") || err.message || t("inventoryDetail.notFound"));
      setListing(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [dealerId, listingId]);

  const photos = useMemo(() => listing?.photos || [], [listing?.photos]);
  const photoEditorLabels = useMemo(
    () => ({
      title: t("inventoryDetail.managePhotosTitle"),
      intro: t("inventoryDetail.managePhotosIntro"),
      save: t("inventoryDetail.savePhotos"),
      saving: t("inventoryDetail.savingPhotos"),
      saveFailed: t("inventoryDetail.savePhotosFailed"),
      helper: t("inventoryForm.photos.helper"),
      empty: t("inventoryForm.photos.empty"),
      primaryBadge: t("inventoryForm.photos.primaryBadge"),
      addUrl: t("inventoryForm.photos.addUrl"),
      orAddUrl: t("inventoryForm.photos.orAddUrl"),
      addUrlPlaceholder: t("inventoryForm.photos.addUrlPlaceholder"),
      uploadHint: t("inventoryForm.photos.uploadHint"),
      uploadFiles: t("inventoryForm.photos.uploadFiles"),
      uploadingFiles: t("inventoryForm.photos.uploadingFiles"),
      uploadedFileNote: t("inventoryForm.photos.uploadedFileNote"),
      uploadedFilePlaceholder: t("inventoryForm.photos.uploadedFilePlaceholder"),
      moveUp: t("inventoryForm.photos.moveUp"),
      moveDown: t("inventoryForm.photos.moveDown"),
      setPrimary: t("inventoryForm.photos.setPrimary"),
      remove: t("inventoryForm.photos.remove"),
      count: t("inventoryForm.photos.count"),
      invalidUrl: t("inventoryForm.photos.invalidUrl"),
      tooMany: t("inventoryForm.photos.tooMany"),
      tooLarge: t("inventoryForm.photos.tooLarge"),
      invalidType: t("inventoryForm.photos.invalidType"),
      emptyFile: t("inventoryForm.photos.emptyFile"),
      uploadFailed: t("inventoryForm.photos.uploadFailed"),
      sectionsPhotos: t("inventoryForm.sections.photos"),
    }),
    [t]
  );
  const heroPhoto = photos[selectedPhoto]?.url || photos[0]?.url || null;

  const runAction = async (action) => {
    setActionBusy(true);
    setError("");
    try {
      await runDealerListingAction(dealerId, listingId, action);
      trackInventoryEvent(`inventory_listing_${action}`, { listingId, dealerId });
      await load();
    } catch (err) {
      setError(translateError(err.message, "actionFailed") || err.message || t("inventoryDetail.actionFailed"));
    } finally {
      setActionBusy(false);
    }
  };

  const requestFeatured = async () => {
    setError("");
    try {
      await requestFeaturedPlacement(dealerId, {
        listingId,
        requestedPlacementType: "featured_listing",
        message: `Solicitud destacar listing ${listingId}`,
      });
      trackFeaturedPlacementEvent("dealer_featured_request_created", { dealerId, listingId });
    } catch (err) {
      setError(err.message || t("inventoryDetail.actionFailed"));
    }
  };

  if (loading) {
    return <DetailSkeleton />;
  }

  if (!listing) {
    return (
      <div className="dealer-listing-detail">
        <div className="dealer-panel dealer-listing-detail__empty">
          <p>{error || t("inventoryDetail.notFound")}</p>
          <Link className="dealer-portal-btn" to="/v/automap/dealer/inventario">
            {t("inventoryDetail.backToInventory")}
          </Link>
        </div>
      </div>
    );
  }

  const title = listingTitle(listing);
  const lastConfirmed = formatDate(listing.lastConfirmedAt, locale) || t("inventoryDetail.neverConfirmed");
  const fairPriceLabel =
    FAIR_PRICE_LABELS_ES[fairPrice?.fairPriceLabel] || fairPrice?.fairPriceLabel || "—";
  const showHighPriceHint =
    fairPrice?.fairPriceLabel === "high_price" || fairPrice?.fairPriceLabel === "slightly_high";

  return (
    <div className="dealer-listing-detail">
      <header className="dealer-listing-detail__topbar">
        <Link className="dealer-listing-detail__back" to="/v/automap/dealer/inventario">
          {t("inventoryDetail.backToInventory")}
        </Link>
        <div className="dealer-listing-detail__topbar-actions">
          <Link className="dealer-portal-btn primary" to={`/v/automap/dealer/inventario/${listing.id}/editar`}>
            ✏️ {t("inventoryDetail.editListing")}
          </Link>
          {listing.status === "active" ? (
            <DealerListingPublicAction
              listing={listing}
              locale={locale}
              t={t}
              dealerId={dealerId}
              showUnavailableHint={false}
            />
          ) : null}
        </div>
      </header>

      {error ? <p className="dealer-listing-detail__error">{error}</p> : null}

      <section className="dealer-listing-detail__hero panel-card">
        <div className="dealer-listing-detail__hero-grid">
          <div className="dealer-listing-detail__gallery">
            <div className="dealer-listing-detail__hero-photo">
              {heroPhoto ? (
                <img
                  src={heroPhoto}
                  alt={title}
                  onError={(event) => {
                    event.currentTarget.src = LISTING_PLACEHOLDER_IMAGE;
                  }}
                />
              ) : (
                <div className="dealer-listing-detail__hero-placeholder">{t("inventory.noPhoto")}</div>
              )}
              <span
                className={`dealer-listing-detail__status dealer-listing-detail__status--${statusTone(listing.status)}`}
              >
                {formatListingStatus(listing.status)}
              </span>
            </div>
            {photos.length > 1 ? (
              <ul className="dealer-listing-detail__thumbs" aria-label={t("inventoryDetail.photosTitle")}>
                {photos.map((photo, index) => (
                  <li key={photo.url || index}>
                    <button
                      type="button"
                      className={index === selectedPhoto ? "is-active" : undefined}
                      onClick={() => setSelectedPhoto(index)}
                      aria-label={`${t("inventoryDetail.photosTitle")} ${index + 1}`}
                    >
                      <img
                        src={photo.url}
                        alt=""
                        onError={(event) => {
                          event.currentTarget.src = LISTING_PLACEHOLDER_IMAGE;
                        }}
                      />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {!photos.length ? (
              <p className="dealer-listing-detail__photo-hint muted">{t("inventoryDetail.noPhotos")}</p>
            ) : null}
          </div>

          <div className="dealer-listing-detail__summary">
            <p className="dealer-listing-detail__eyebrow">{listing.city || "—"}</p>
            <h1 className="dealer-listing-detail__title">{title}</h1>
            <p className="dealer-listing-detail__price">{formatMoney(listing.priceCop)}</p>

            <div className="dealer-listing-detail__badges">
              <span className="dealer-listing-detail__badge">
                {t("inventory.availability")}: {formatListingStatus(listing.availabilityStatus)}
              </span>
              <span className={`admin-badge ${qualityBadgeClass(listing.qualityScore)}`}>
                ⭐ {listing.qualityScore} · {listing.qualityLabel}
              </span>
              {featuredInfo ? (
                <span className="dealer-listing-detail__badge dealer-listing-detail__badge--featured">
                  ✨ {t("inventoryDetail.featuredActive")}
                </span>
              ) : null}
            </div>

            <dl className="dealer-listing-detail__quick-specs">
              <div>
                <dt>{t("inventory.mileage")}</dt>
                <dd>{listing.mileageKm != null ? `${listing.mileageKm.toLocaleString(locale)} km` : "—"}</dd>
              </div>
              <div>
                <dt>{t("inventoryDetail.transmission")}</dt>
                <dd>{listing.transmission || "—"}</dd>
              </div>
              <div>
                <dt>{t("inventoryDetail.fuel")}</dt>
                <dd>{listing.fuel || "—"}</dd>
              </div>
              <div>
                <dt>{t("inventoryDetail.bodyType")}</dt>
                <dd>{listing.bodyType || "—"}</dd>
              </div>
            </dl>

            {!featuredInfo ? (
              <button type="button" className="dealer-portal-btn" disabled={actionBusy} onClick={requestFeatured}>
                ✨ {t("inventoryDetail.requestFeatured")}
              </button>
            ) : (
              <p className="dealer-listing-detail__featured-note muted">
                {t("inventoryDetail.featuredUntil", { date: featuredInfo.endAt?.slice(0, 10) || "—" })}
              </p>
            )}
          </div>
        </div>
      </section>

      <div className="dealer-listing-detail__stats">
        <StatCard label={t("inventoryDetail.statsViews")} value={listing.viewCount ?? 0} />
        <StatCard label={t("inventoryDetail.statsLeads")} value={listing.leadCount ?? 0} />
        <StatCard
          label={t("inventoryDetail.statsQuality")}
          value={`${listing.qualityScore ?? "—"}/100`}
          hint={listing.qualityLabel}
        />
        <StatCard label={t("inventoryDetail.statsLastConfirmed")} value={lastConfirmed} />
      </div>

      <div className="dealer-listing-detail__layout">
        <div className="dealer-listing-detail__main">
          <DealerListingPhotoEditor
            dealerId={dealerId}
            listingId={listing.id}
            initialPhotos={photos}
            disabled={actionBusy}
            labels={photoEditorLabels}
            onSaved={async () => {
              setPhotoEditorError("");
              await load();
            }}
            onError={setPhotoEditorError}
          />
          {photoEditorError ? <p className="dealer-listing-detail__error">{photoEditorError}</p> : null}

          <section className="dealer-panel">
            <h2>{t("inventoryDetail.descriptionTitle")}</h2>
            <p className="dealer-listing-detail__description">
              {listing.description || t("inventoryDetail.noDescription")}
            </p>
          </section>

          <section className="dealer-panel">
            <h2>{t("inventoryDetail.specsTitle")}</h2>
            <div className="dealer-listing-detail__spec-grid">
              <SpecItem label={t("inventoryDetail.version")} value={listing.version} />
              <SpecItem label={t("inventoryDetail.color")} value={listing.color} />
              <SpecItem label={t("inventory.city")} value={listing.city} />
              <SpecItem label={t("inventory.mileage")} value={listing.mileageKm?.toLocaleString(locale)} />
              <SpecItem label={t("inventoryDetail.transmission")} value={listing.transmission} />
              <SpecItem label={t("inventoryDetail.fuel")} value={listing.fuel} />
              <SpecItem label={t("inventoryDetail.bodyType")} value={listing.bodyType} />
            </div>
          </section>

          <DealerListingBuyerInterestSection dealerId={dealerId} listingId={listing.id} />
        </div>

        <aside className="dealer-listing-detail__aside">
          <section className="dealer-panel dealer-listing-detail__workflow">
            <h2>{t("inventoryDetail.workflowTitle")}</h2>
            <p className="muted">{t("inventoryDetail.workflowIntro")}</p>
            <div className="dealer-listing-detail__workflow-actions">
              <button
                type="button"
                className="dealer-portal-btn primary"
                disabled={actionBusy}
                onClick={() => runAction("confirm_available")}
              >
                ✓ {t("inventoryDetail.confirmAvailable")}
              </button>
              <button type="button" className="dealer-portal-btn" disabled={actionBusy} onClick={() => runAction("mark_reserved")}>
                {t("inventoryDetail.markReserved")}
              </button>
              <button type="button" className="dealer-portal-btn" disabled={actionBusy} onClick={() => runAction("mark_sold")}>
                {t("inventoryDetail.markSold")}
              </button>
              <button type="button" className="dealer-portal-btn" disabled={actionBusy} onClick={() => runAction("pause")}>
                {t("inventoryDetail.pause")}
              </button>
              <button type="button" className="dealer-portal-btn" disabled={actionBusy} onClick={() => runAction("reactivate")}>
                {t("inventoryDetail.reactivate")}
              </button>
              <button type="button" className="dealer-portal-btn" disabled={actionBusy} onClick={() => runAction("archive")}>
                {t("inventoryDetail.archive")}
              </button>
            </div>
          </section>

          {fairPrice ? (
            <section className="dealer-panel dealer-listing-detail__insight">
              <h2>{t("inventoryDetail.fairPriceTitle")}</h2>
              <FairPriceBadge listing={fairPrice} variant="admin" hideIfNoData={false} showTooltip={false} />
              <dl className="dealer-listing-detail__insight-list">
                <div>
                  <dt>{t("inventoryDetail.fairPriceLabel")}</dt>
                  <dd>{fairPriceLabel}</dd>
                </div>
                <div>
                  <dt>{t("inventoryDetail.fairPriceMedian")}</dt>
                  <dd>{fairPrice.medianComparablePriceCop != null ? formatMoney(fairPrice.medianComparablePriceCop) : "—"}</dd>
                </div>
                <div>
                  <dt>{t("inventoryDetail.fairPriceComparables")}</dt>
                  <dd>{fairPrice.comparableCount ?? 0}</dd>
                </div>
                <div>
                  <dt>{t("inventoryDetail.fairPriceDelta")}</dt>
                  <dd>{fairPrice.priceDeltaPercent != null ? `${fairPrice.priceDeltaPercent}%` : "—"}</dd>
                </div>
              </dl>
              {showHighPriceHint ? <p className="muted">{t("inventoryDetail.fairPriceHighHint")}</p> : null}
            </section>
          ) : null}

          {vehicleRisk ? (
            <section className="dealer-panel dealer-listing-detail__insight">
              <h2>{t("inventoryDetail.riskTitle")}</h2>
              <p className="muted">{t("inventoryDetail.riskIntro")}</p>
              <RiskLevelPill riskLevel={vehicleRisk.riskLevel} />
              {vehicleRisk.dealerVisibleIndicators?.length ? (
                <ul className="dealer-listing-detail__risk-list">
                  {vehicleRisk.dealerVisibleIndicators.map((item) => (
                    <li key={item.type}>
                      <strong>{item.message}</strong>
                      <span>{getDealerFixSuggestion(item.type)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">{t("inventoryDetail.riskNone")}</p>
              )}
              <p className="dealer-listing-detail__inline-links">
                <Link to={`/v/automap/dealer/inventario/${listing.id}/editar`}>{t("inventoryDetail.riskEditLink")}</Link>
                {" · "}
                <Link to="/v/automap/dealer/inventario/calidad">{t("inventoryDetail.riskQualityLink")}</Link>
              </p>
            </section>
          ) : null}

          {listing.missingFields?.length ? (
            <section className="dealer-panel dealer-listing-detail__warnings">
              <h2>{t("inventoryDetail.warningsTitle")}</h2>
              <ul>
                {listing.missingFields.map((field) => (
                  <li key={field}>{field}</li>
                ))}
              </ul>
              <Link className="dealer-portal-btn" to={`/v/automap/dealer/inventario/${listing.id}/editar`}>
                {t("inventoryDetail.editListing")}
              </Link>
            </section>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
